import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import StarIcon from "@mui/icons-material/Star";

import { apiRequest } from "../../api/api";
import useIsMobile from "../../shared_modules/useIsMobile";
import { useTenant } from "../../hooks/useTenant";
import CalendarCard from "../client/calendarCard";
import AvailableTimes from "./availableTimes";
import { getSubdomain } from "../../utils/getSubdomain";
import { formatTime, formatPhoneNumber } from "../../utils/functions";

const ManageBooking = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  // The reschedule endpoint rotates the magic-link token; track the live one so
  // subsequent cancel/reschedule actions use the valid token, not the stale URL.
  const [activeToken, setActiveToken] = useState(token);
  const isMobile = useIsMobile();
  const { tenant } = useTenant();
  const domain = getSubdomain();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingData, setBookingData] = useState(null);

  // Cancel dialog
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelResult, setCancelResult] = useState(null);

  // Reschedule dialog
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [rescheduleSlot, setRescheduleSlot] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleError, setRescheduleError] = useState(null);
  const [rescheduleResult, setRescheduleResult] = useState(null);

  useEffect(() => {
    if (!token) {
      setError(
        "No booking token found. Please check your email for the correct link.",
      );
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        const res = await apiRequest(
          `/guests/booking.php?token=${encodeURIComponent(token)}`,
        );
        setBookingData(res.data);
      } catch (e) {
        setError(e.message || "Could not load your booking.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleCancel = async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      const res = await apiRequest("/guests/cancel.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: activeToken }),
      });
      setCancelResult(res.data);
      setCancelOpen(false);
      // Update local booking status
      setBookingData((prev) => ({
        ...prev,
        booking: { ...prev.booking, status: "cancelled" },
        actions: { ...prev.actions, can_cancel: false, can_reschedule: false },
      }));
    } catch (e) {
      setCancelError(
        e.message || "Could not cancel your booking. Please try again.",
      );
    } finally {
      setCancelling(false);
    }
  };

  const handleReschedule = async () => {
    if (!rescheduleSlot) return;
    setRescheduling(true);
    setRescheduleError(null);
    try {
      const res = await apiRequest("/guests/reschedule.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: activeToken,
          starts_at: rescheduleSlot.start_utc,
        }),
      });
      // The token is rotated on reschedule — adopt the new one for future actions.
      const nextToken = res.data?.token || activeToken;
      setActiveToken(nextToken);
      // Re-fetch full booking so actions/window/cancel_by all reflect the new time
      const updated = await apiRequest(
        `/guests/booking.php?token=${encodeURIComponent(nextToken)}`,
      );
      setBookingData(updated.data);
      setRescheduleOpen(false);
      setRescheduleSlot(null);
      setRescheduleResult(
        "Your appointment has been rescheduled successfully.",
      );
    } catch (e) {
      setRescheduleError(
        e.message || "Could not reschedule. Please try again.",
      );
    } finally {
      setRescheduling(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading your booking...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          px: 3,
          gap: 2,
        }}
      >
        <Typography variant="h3" sx={{ textAlign: "center" }}>
          Link not found
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", textAlign: "center", maxWidth: 400 }}
        >
          {error}
        </Typography>
      </Box>
    );
  }

  const { booking, barber, service, shop, formatted, actions } = bookingData;

  const isCancelled = booking?.status === "cancelled";
  const startsAt = booking?.starts_at
    ? new Date(booking.starts_at.replace(" ", "T") + "Z")
    : null;
  const tz = tenant?.timezone || "America/New_York";

  const calcFee = (price) => {
    const p = Number(price);
    const feeRate = tenant?.processing_fee_rate ?? 0.025;
    const subtotal = p + Math.round(p * feeRate * 100) / 100;
    return (Math.round(((subtotal + 0.3) / 0.971) * 100) / 100).toFixed(2);
  };

  const barberForSlots = barber
    ? {
        user_id: barber.user_id,
        first_name: barber.first_name,
        last_name: barber.last_name,
      }
    : null;
  const serviceForSlots = service
    ? {
        service_id: service.service_id,
        name: service.name,
        duration_minutes: service.duration_minutes,
      }
    : null;

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        minHeight: "calc(100vh - 64px)",
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
        px: isMobile ? 3 : 4,
        py: 6,
      }}
    >
      {/* Header */}
      <Box
        sx={{ width: "100%", maxWidth: "620px", mb: 4, textAlign: "center" }}
      >
        {!isCancelled ? (
          <>
            {rescheduleResult && (
              <Alert
                severity="success"
                sx={{ mb: 3, textAlign: "left" }}
                onClose={() => setRescheduleResult(null)}
              >
                {rescheduleResult}
              </Alert>
            )}
            {cancelResult && (
              <Alert
                severity="success"
                sx={{ mb: 3, textAlign: "left" }}
                onClose={() => setCancelResult(null)}
              >
                {cancelResult.message}
              </Alert>
            )}
            <Typography variant="h2" sx={{ mb: 5 }}>
              Upcoming Appointment
            </Typography>
            <Box sx={{ mb: 2 }} />
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelOutlinedIcon />}
                onClick={() => setCancelOpen(true)}
              >
                Cancel Appointment
              </Button>
              {actions?.can_reschedule && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EventAvailableIcon />}
                  onClick={() => setRescheduleOpen(true)}
                >
                  Reschedule
                </Button>
              )}
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <CancelOutlinedIcon
                sx={{ color: "text.secondary", fontSize: "1.1rem" }}
              />
              <Typography
                variant="overline"
                sx={{ color: "text.secondary", fontWeight: 600 }}
              >
                Cancelled
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ mb: 0.5 }}>
              Booking Cancelled
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              This appointment has been cancelled.
            </Typography>
          </>
        )}
      </Box>

      {/* Booking summary card */}
      <Box
        component={Paper}
        elevation={1}
        sx={{
          width: "100%",
          maxWidth: "620px",
          borderRadius: 2,
          border: "0.1px solid",
          borderColor: "customDivider",
          backgroundColor: "offWhite",
          p: 3,
          opacity: isCancelled ? 0.6 : 1,
        }}
      >
        {/* Barber row */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: "4rem",
                height: "4rem",
                borderRadius: "50%",
                backgroundColor: "background.default",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.2rem",
                fontWeight: 700,
                color: "text.third",
                border: "1px solid",
                borderColor: "customDivider",
                flexShrink: 0,
                overflow: "hidden",
              }}
            >
              {barber?.avatar_url ? (
                <Box
                  component="img"
                  src={barber.avatar_url}
                  alt={`${barber.first_name} ${barber.last_name}`}
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <>
                  {barber?.first_name?.[0]}
                  {barber?.last_name?.[0]}
                </>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {barber?.first_name} {barber?.last_name}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: isCancelled
                ? "rgba(0,0,0,0.08)"
                : "success.main" + "20",
              px: 1.5,
              py: 0.5,
              borderRadius: "20px",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isCancelled ? "text.secondary" : "success.main",
                fontWeight: 600,
              }}
            >
              {isCancelled ? "CANCELLED" : "CONFIRMED"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "customDivider", mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: "Service", value: service?.name },
            {
              label: "Duration",
              value: service?.duration_minutes
                ? `${service.duration_minutes} min`
                : "—",
            },
            {
              label: "Date",
              value: startsAt
                ? startsAt.toLocaleDateString("en-US", {
                    timeZone: tz,
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })
                : "—",
            },
            {
              label: "Time",
              value: startsAt
                ? startsAt.toLocaleTimeString("en-US", {
                    timeZone: tz,
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  })
                : "—",
            },
          ].map(({ label, value }) => (
            <Grid key={label} size={isMobile ? 6 : 3}>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 0.5 }}
              >
                {label}
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {value || "—"}
              </Typography>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ borderColor: "customDivider", mb: 2 }} />

        {/* Payment section */}
        {booking?.payment_method === "card" &&
          Number(booking.tip_amount) > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Service price
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  ${Number(booking.price_charged).toFixed(2)}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Tip for barber
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: "#C4984D" }}
                >
                  +${Number(booking.tip_amount).toFixed(2)}
                </Typography>
              </Box>
              <Divider
                sx={{ mt: 1.5, mb: 1.5, borderColor: "customDivider" }}
              />
            </Box>
          )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {booking?.payment_method === "card"
              ? "Total charged"
              : "Due at appointment"}
          </Typography>
          <Typography variant="h4" sx={{ color: "text.third" }}>
            {booking?.payment_method === "card"
              ? `$${Number(booking.amount_paid ?? booking.price_charged).toFixed(2)}`
              : `$${Number(booking.price_charged).toFixed(2)} cash`}
          </Typography>
        </Box>

        {/* Cancellation deadline */}
        {!isCancelled && formatted?.cancel_by && (
          <>
            <Divider sx={{ borderColor: "customDivider", mb: 2 }} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.78rem",
                lineHeight: 1.6,
              }}
            >
              Free cancellation until{" "}
              <strong style={{ color: "#111008" }}>
                {formatted.cancel_by}
              </strong>
              .{" "}
              {!actions?.within_window && (
                <span style={{ color: "#e53935" }}>
                  {booking?.payment_method === "card"
                    ? "You are past the free cancellation window — your payment is non-refundable."
                    : `You are past the free cancellation window — a $${calcFee(booking?.price_charged)} fee will be charged.`}
                </span>
              )}
            </Typography>
          </>
        )}
      </Box>

      {/* Location card */}
      {(tenant?.address_line1 || tenant?.city) && (
        <Box
          component={Paper}
          elevation={1}
          sx={{
            width: "100%",
            maxWidth: "620px",
            borderRadius: 2,
            border: "0.1px solid",
            borderColor: "customDivider",
            backgroundColor: "offWhite",
            mt: 3,
            overflow: "hidden",
          }}
        >
          {(() => {
            const addressParts = [
              tenant?.address_line1,
              tenant?.address_line2,
              tenant?.city,
              tenant?.state,
              tenant?.zip,
            ].filter(Boolean);
            const addressQuery = encodeURIComponent(addressParts.join(", "));
            return (
              <Box sx={{ width: "100%", height: 200 }}>
                <iframe
                  title="Shop Location"
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: "block" }}
                  loading="lazy"
                  allowFullScreen
                  src={`https://www.google.com/maps?q=${addressQuery}&output=embed`}
                />
              </Box>
            );
          })()}
          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {tenant?.name}
            </Typography>
            {tenant?.address_line1 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {tenant.address_line1}
                {tenant.address_line2 ? `, ${tenant.address_line2}` : ""}
              </Typography>
            )}
            {(tenant?.city || tenant?.state || tenant?.zip) && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {[tenant?.city, tenant?.state, tenant?.zip]
                  .filter(Boolean)
                  .join(", ")}
              </Typography>
            )}
            {tenant?.phone && (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {formatPhoneNumber(tenant.phone)}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* ── Cancel confirmation dialog ── */}
      <Dialog
        open={cancelOpen}
        onClose={() => !cancelling && setCancelOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Appointment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
            Are you sure you want to cancel your appointment on{" "}
            <strong>{formatted?.starts_at}</strong>?
          </Typography>
          {!actions?.within_window && (
            <Alert severity="warning" sx={{ mt: 1, fontSize: "0.82rem" }}>
              You're past the free cancellation window.{" "}
              {booking?.payment_method === "card" ? (
                "Your payment is non-refundable. The barber has already been paid."
              ) : (
                <>
                  A cancellation fee of{" "}
                  <strong>${calcFee(booking?.price_charged)}</strong> will be
                  charged to your card on file.
                </>
              )}
            </Alert>
          )}
          {cancelError && (
            <Alert severity="error" sx={{ mt: 1.5, fontSize: "0.82rem" }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setCancelOpen(false)}
            disabled={cancelling}
          >
            Keep Appointment
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : (
              "Yes, Cancel"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reschedule dialog ── */}
      <Dialog
        open={rescheduleOpen}
        onClose={() => !rescheduling && setRescheduleOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Reschedule Appointment
        </DialogTitle>
        <DialogContent sx={{ "&&": { pt: 2 } }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
            Select a new date and time with{" "}
            <strong>
              {barber?.first_name} {barber?.last_name}
            </strong>
            .
          </Typography>

          <Grid container spacing={2}>
            <Grid size={isMobile ? 12 : 6}>
              <CalendarCard
                selected={rescheduleDate}
                setSelected={(d) => {
                  setRescheduleDate(d);
                  setRescheduleSlot(null);
                }}
              />
            </Grid>
            <Grid size={isMobile ? 12 : 6}>
              {barberForSlots && serviceForSlots && (
                <AvailableTimes
                  barber={barberForSlots}
                  service={serviceForSlots}
                  selected={rescheduleDate}
                  selectedSlot={rescheduleSlot}
                  setSelectedSlot={setRescheduleSlot}
                />
              )}
            </Grid>
          </Grid>

          {rescheduleError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {rescheduleError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setRescheduleOpen(false)}
            disabled={rescheduling}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReschedule}
            disabled={!rescheduleSlot || rescheduling}
          >
            {rescheduling ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : rescheduleSlot ? (
              `Confirm — ${formatTime(rescheduleSlot.start)}`
            ) : (
              "Select a time"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageBooking;
